import { Dispatch, useEffect, useRef, useState } from 'react';

import { Card } from './card';
import { formatProvidersList, getFullName, buildSpiNpiStringForProvider } from '../../../helpers';

import { Checkbox } from '@/shared/ui';
import { makeRequest } from '@/shared/services/http';
import { IProvider } from '@/shared/types/interfaces/provider';
import { useAppSelector } from '@/shared/hooks/store';
import { selectPrescriptionsProviders } from '@/store/slices/onboarding/selectors';
import { FormLabel } from '../../formLabel';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { scrollToElement } from '@/shared/helpers/scroll';
import { getConvertedProviderSearch } from '@/shared/helpers/providers';

import HelpArrowRotatedIcon from '@/assets/icons/help-arrow-rotated.svg';
import { AdvancedAutoComplete } from '@/shared/ui/autocomplete/adavanced-autoComplete';


interface IProps {
    isEditMode?: boolean;
    selectedProvider: IProvider | null;
    setSelectedProvider: Dispatch<React.SetStateAction<IProvider | null>>;
    error?: boolean;
    zipCode: string;
    disabled?: boolean;
}

const AutoCompleteWrapper = ({
    withoutLabel = false,
    error,
    loadOptionList,
    onSelect
}: {
    withoutLabel?: boolean;
    error?: boolean;
    loadOptionList: (query: string, signal: AbortSignal) => Promise<IProvider>;
    onSelect(value: IProvider): void;
}) => (
    <AdvancedAutoComplete<IProvider>
        required
        scrollToTitle
        uniqueIdentifier="providerFinder"
        labelKey="display"
        className="mb-[10px]"
        label={!withoutLabel ? 'Who prescribes this medication?' : ''}
        placeholder="ex. Fran Lewis, Orlando FL"
        subLabel={
            <span className="font-semibold">
                Search by <span className="underline">last name, then city & state</span>
            </span>
        }
        inputVariant="advanced"
        popoverInputVariant="advanced"
        popoverTitle="Search for your doctor"
        loadOptionsList={loadOptionList}
        onSelect={onSelect}
        error={error}
        popoverHelper={
            <i className="absolute right-[26px] z-[10] flex items-start font-dmSans text-[1.0625rem] tracking-[0.02rem]">
                <p className="mr-2 mt-2">
                    It's <b>3x faster</b> <br /> using voice!
                </p>
                <img src={HelpArrowRotatedIcon} alt="help arrow" className="w-[53px]" />
            </i>
        }
    />
);

export const Provider = ({ isEditMode, selectedProvider, error, zipCode: zip = '', setSelectedProvider, disabled }: IProps) => {
    const [showAnotherProviderSelection, setShowAnotherProviderSelection] = useState(false);
    const existingProviders = useAppSelector(selectPrescriptionsProviders);

    const [providersList, setProvidersList] = useState<IProvider[]>(existingProviders || []);
    const [anotherProvider, setAnotherProvider] = useState<IProvider | null>(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const wrapperRef = useRef<HTMLDivElement | null>(null);

    const isFirstProvider = useRef<boolean>(!providersList.length);

    const loadProviders = async (query: string, signal: AbortSignal) => {
        const term = getConvertedProviderSearch(query);
        if (term.length < 2) return [];
        const providers = await makeRequest<IProvider[]>('get', 'providers', 'search', { params: { term, zip }, signal });

        return formatProvidersList(providers).filter(
            item => {
                return !providersList.find(prov => prov.npi === item.npi && prov.spi === item.spi)
            }
        );
    };

    useEffect(() => {
        if (isMobile && selectedProvider && wrapperRef.current) {
            scrollToElement(wrapperRef.current!, 100, 0);
        }
    }, [wrapperRef.current, isMobile, selectedProvider]);

    const onSelectProvider = (provider: IProvider, addToList = true) => {
        setShowAnotherProviderSelection(false);
        if (addToList) setAnotherProvider(provider);

        if (selectedProvider && buildSpiNpiStringForProvider(selectedProvider!) === buildSpiNpiStringForProvider(provider)) {
            return setSelectedProvider(null);
        }

        setSelectedProvider(provider);
        if (addToList) setProvidersList(providersList.concat(provider));
    };

    const onChooseAnotherLocation = () => {
        setSelectedProvider(null);
        setShowAnotherProviderSelection(!showAnotherProviderSelection);
    };

    const removeSelectedProvider = (provider: IProvider) => {
        setAnotherProvider(null);
        setSelectedProvider(null);
        setProvidersList(providersList.filter(item => item.phoneNumber !== provider.phoneNumber));
    };

    const onStartUpdatingProvider = (provider: IProvider) => {
        if (anotherProvider?.phoneNumber !== provider.phoneNumber && !isEditMode) return;

        return () => {
            removeSelectedProvider(provider);
            setShowAnotherProviderSelection(true);
        };
    };

    if (!selectedProvider && !providersList.length)
        return <AutoCompleteWrapper loadOptionList={loadProviders} onSelect={onSelectProvider} error={error} />;

    return (
        <div data-testid="prescription-medication-form-provider" ref={wrapperRef}>
            <FormLabel required label="Who prescribes this medication?" className="mb-[15px]" />
            <div className="mb-3 mt-3 flex flex-col gap-[10px] rounded-[15px] border-2 bg-mint-200 p-[15px]">
                {providersList?.map(provider => (
                    <div data-testid="prescription-medication-form-provider" key={buildSpiNpiStringForProvider(provider)} className="flex w-full gap-[20px]">
                        {!isFirstProvider.current && (
                            <Checkbox
                                className="prescriptions__provider_checkbox"
                                checked={!!(selectedProvider && (buildSpiNpiStringForProvider(selectedProvider!) === buildSpiNpiStringForProvider(provider)))}
                                onChange={() => onSelectProvider(provider, false)}
                                error={error}
                                testId="prescription-medication-form-provider-select"
                            />
                        )}
                        <Card
                            testId="prescription-medication-form-provider-card"
                            name={getFullName(provider)}
                            addressLine1={provider.address.addressLine1}
                            addressLine2={provider.address.addressLine2}
                            onUpdate={onStartUpdatingProvider(provider)}
                            disabled={disabled}
                        />
                    </div>
                ))}
                {!isEditMode && !isFirstProvider.current && !anotherProvider && (
                    <div className="flex w-full gap-[20px]">
                        <Checkbox
                            checked={showAnotherProviderSelection}
                            className="prescriptions__provider_checkbox gap-[20px] lg:gap-[20px]"
                            error={error}
                            onChange={onChooseAnotherLocation}
                            label="I have another prescriber for this medication"
                            testId="prescription-medication-form-provider-another-prescriber"
                        />
                    </div>
                )}
                {showAnotherProviderSelection && (
                    <AutoCompleteWrapper withoutLabel loadOptionList={loadProviders} onSelect={onSelectProvider} />
                )}
            </div>
        </div>
    );
};
